/**
 * Business Health Score Calculation Utility
 * Calculates overall business health on 0-100 scale using weighted metrics
 */

import i18n from '../lib/i18n'
import { 
  getProfitMarginStatus, 
  getConversionRateStatus, 
  getRevenueGrowthStatus, 
  getExpenseRatioStatus 
} from './performanceThresholds'

/**
 * Metric weights for health score calculation
 * Total should equal 100
 */
const HEALTH_SCORE_WEIGHTS = {
  profitMargin: 25,    // Most important - profitability
  conversionRate: 20,  // Sales efficiency  
  revenueGrowth: 20,   // Growth trajectory
  expenseRatio: 20,    // Cost management (inverted)
  netProfit: 15        // Absolute profitability
}

/**
 * Status to score mapping
 */
const STATUS_SCORES = {
  excellent: 100,
  good: 75,
  warning: 50,
  poor: 25,
  neutral: 0
}

/**
 * Calculate individual metric score based on performance status
 */
const getMetricScore = (metricType, value) => {
  let status = 'neutral'
  
  switch (metricType) {
    case 'profitMargin':
      status = getProfitMarginStatus(value)
      break
    case 'conversionRate':
      status = getConversionRateStatus(value)
      break
    case 'revenueGrowth':
      status = getRevenueGrowthStatus(value)
      break
    case 'expenseRatio':
      status = getExpenseRatioStatus(value)
      break
    case 'netProfit':
      // Net profit scoring based on value ranges
      if (value >= 50000) status = 'excellent'
      else if (value >= 20000) status = 'good'
      else if (value >= 5000) status = 'warning'
      else if (value > 0) status = 'poor'
      else status = 'poor'
      break
    default:
      status = 'neutral'
  }
  
  return STATUS_SCORES[status] || 0
}

/**
 * Calculate overall business health score
 */
export const calculateHealthScore = (metrics) => {
  if (!metrics || typeof metrics !== 'object') {
    return {
      score: 0,
      status: 'neutral',
      breakdown: {},
      recommendations: []
    }
  }

  const breakdown = {}
  let totalScore = 0

  // Calculate weighted scores for each metric
  Object.keys(HEALTH_SCORE_WEIGHTS).forEach(metricType => {
    const value = metrics[metricType]
    const weight = HEALTH_SCORE_WEIGHTS[metricType]
    const metricScore = getMetricScore(metricType, value)
    const weightedScore = (metricScore * weight) / 100
    
    breakdown[metricType] = {
      value,
      score: metricScore,
      weight,
      weightedScore,
      status: getMetricStatus(metricType, value)
    }
    
    totalScore += weightedScore
  })

  // Determine overall status
  const overallStatus = getOverallStatus(totalScore)
  
  // Generate recommendations
  const recommendations = generateRecommendations(breakdown, totalScore)

  return {
    score: Math.round(totalScore),
    status: overallStatus,
    breakdown,
    recommendations
  }
}

/**
 * Get metric status (reuse from performanceThresholds)
 */
const getMetricStatus = (metricType, value) => {
  switch (metricType) {
    case 'profitMargin':
      return getProfitMarginStatus(value)
    case 'conversionRate':
      return getConversionRateStatus(value)
    case 'revenueGrowth':
      return getRevenueGrowthStatus(value)
    case 'expenseRatio':
      return getExpenseRatioStatus(value)
    case 'netProfit':
      if (value >= 50000) return 'excellent'
      else if (value >= 20000) return 'good'
      else if (value >= 5000) return 'warning'
      else if (value > 0) return 'poor'
      else return 'poor'
    default:
      return 'neutral'
  }
}

/**
 * Determine overall health status based on score
 */
const getOverallStatus = (score) => {
  if (score >= 85) return 'excellent'
  else if (score >= 70) return 'good'
  else if (score >= 50) return 'warning'
  else return 'poor'
}

/**
 * Generate actionable recommendations based on health breakdown
 */
const generateRecommendations = (breakdown, totalScore) => {
  const recommendations = []
  
  // Find the lowest performing metrics
  const sortedMetrics = Object.entries(breakdown)
    .sort((a, b) => a[1].score - b[1].score)
  
  const lowestMetric = sortedMetrics[0]
  const secondLowest = sortedMetrics[1]
  
  // Generate specific recommendations based on poor performing metrics
  if (lowestMetric[1].status === 'poor' || lowestMetric[1].status === 'warning') {
    recommendations.push(getMetricRecommendation(lowestMetric[0], lowestMetric[1]))
  }
  
  if (secondLowest[1].status === 'poor') {
    recommendations.push(getMetricRecommendation(secondLowest[0], secondLowest[1]))
  }
  
  // Overall health recommendations
  if (totalScore < 50) {
    recommendations.unshift({
      type: 'critical',
      title: i18n.t('healthScore.recommendations.critical.title', { ns: 'dashboard' }),
      message: i18n.t('healthScore.recommendations.critical.message', { ns: 'dashboard' }),
      priority: 'high'
    })
  } else if (totalScore < 70) {
    recommendations.unshift({
      type: 'warning',
      title: i18n.t('healthScore.recommendations.warning.title', { ns: 'dashboard' }),
      message: i18n.t('healthScore.recommendations.warning.message', { ns: 'dashboard' }),
      priority: 'medium'
    })
  } else if (totalScore >= 85) {
    recommendations.unshift({
      type: 'success',
      title: i18n.t('healthScore.recommendations.success.title', { ns: 'dashboard' }),
      message: i18n.t('healthScore.recommendations.success.message', { ns: 'dashboard' }),
      priority: 'low'
    })
  }
  
  return recommendations.slice(0, 3) // Limit to top 3 recommendations
}

/**
 * Get specific recommendation for each metric
 */
const getMetricRecommendation = (metricType, metricData) => {
  const recommendations = {
    profitMargin: {
      type: 'improvement',
      title: i18n.t('healthScore.recommendations.metrics.profitMargin.title', { ns: 'dashboard' }),
      message: i18n.t('healthScore.recommendations.metrics.profitMargin.message', { ns: 'dashboard' }),
      priority: 'high'
    },
    conversionRate: {
      type: 'improvement', 
      title: i18n.t('healthScore.recommendations.metrics.conversionRate.title', { ns: 'dashboard' }),
      message: i18n.t('healthScore.recommendations.metrics.conversionRate.message', { ns: 'dashboard' }),
      priority: 'high'
    },
    revenueGrowth: {
      type: 'improvement',
      title: i18n.t('healthScore.recommendations.metrics.revenueGrowth.title', { ns: 'dashboard' }),
      message: i18n.t('healthScore.recommendations.metrics.revenueGrowth.message', { ns: 'dashboard' }),
      priority: 'medium'
    },
    expenseRatio: {
      type: 'improvement',
      title: i18n.t('healthScore.recommendations.metrics.expenseRatio.title', { ns: 'dashboard' }),
      message: i18n.t('healthScore.recommendations.metrics.expenseRatio.message', { ns: 'dashboard' }),
      priority: 'high'
    },
    netProfit: {
      type: 'improvement',
      title: i18n.t('healthScore.recommendations.metrics.netProfit.title', { ns: 'dashboard' }),
      message: i18n.t('healthScore.recommendations.metrics.netProfit.message', { ns: 'dashboard' }),
      priority: 'high'
    }
  }
  
  return recommendations[metricType] || {
    type: 'general',
    title: i18n.t('healthScore.recommendations.metrics.default.title', { ns: 'dashboard' }),
    message: i18n.t('healthScore.recommendations.metrics.default.message', { ns: 'dashboard' }),
    priority: 'low'
  }
}

/**
 * Calculate health score trend over time
 */
export const calculateHealthScoreTrend = (historicalMetrics) => {
  if (!Array.isArray(historicalMetrics) || historicalMetrics.length < 2) {
    return {
      trend: 'stable',
      change: 0,
      direction: 'neutral'
    }
  }
  
  const healthScores = historicalMetrics.map(metrics => calculateHealthScore(metrics).score)
  const currentScore = healthScores[healthScores.length - 1]
  const previousScore = healthScores[healthScores.length - 2]
  
  const change = currentScore - previousScore
  const changePercent = previousScore !== 0 ? (change / previousScore) * 100 : 0
  
  let trend = 'stable'
  let direction = 'neutral'
  
  if (changePercent > 5) {
    trend = 'improving'
    direction = 'up'
  } else if (changePercent < -5) {
    trend = 'declining'
    direction = 'down'
  }
  
  return {
    trend,
    change: changePercent,
    direction,
    currentScore,
    previousScore
  }
}

/**
 * Get health score color based on score range
 */
export const getHealthScoreColor = (score) => {
  // Keep the *meaning* of the score in the descriptive text/status, but align the
  // visual palette to the app's emerald/green theme.
  //
  // IMPORTANT: these must be literal Tailwind classes (not computed) so they
  // reliably end up in the final CSS bundle.
  if (score >= 85) return { bg: 'bg-emerald-100', text: 'text-emerald-900', progress: 'text-emerald-500' }
  else if (score >= 70) return { bg: 'bg-emerald-50', text: 'text-emerald-900', progress: 'text-emerald-500' }
  else if (score >= 50) return { bg: 'bg-emerald-50', text: 'text-emerald-900', progress: 'text-emerald-400' }
  else return { bg: 'bg-emerald-50', text: 'text-emerald-900', progress: 'text-emerald-400' }
}

/**
 * Format health score with descriptive text
 */
export const formatHealthScore = (score) => {
  const descriptions = {
    excellent: i18n.t('healthScore.status.excellent', { ns: 'dashboard' }),
    good: i18n.t('healthScore.status.good', { ns: 'dashboard' }),
    warning: i18n.t('healthScore.status.warning', { ns: 'dashboard' }),
    poor: i18n.t('healthScore.status.poor', { ns: 'dashboard' })
  }
  
  const status = getOverallStatus(score)
  return {
    score,
    status,
    description: descriptions[status] || i18n.t('healthScore.status.neutral', { ns: 'dashboard' })
  }
}
